/**
 * User Profile Modal
 * Displays detailed stats and recent bets for a leaderboard user
 */

import { TierBadge } from '@components/ui/TierBadge';
import UpgradeModal from '@components/ui/UpgradeModal';
import { ProfileActivitySection } from '@components/ui/user-profile/ProfileActivitySection';
import { ProfileHeader } from '@components/ui/user-profile/ProfileHeader';
import { ProfileStatsSection } from '@components/ui/user-profile/ProfileStatsSection';
import { RecentBetCard } from '@components/ui/user-profile/RecentBetCard';
import { UserListTab } from '@components/ui/user-profile/UserListTab';
import { useAuth } from '@contexts/AuthContext';
import { useToast } from '@contexts/ToastContext';
import { useBlock } from '@hooks/useBlock';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import { useFollow } from '@hooks/useFollow';
import { useFollowers } from '@hooks/useFollowers';
import { useFollowing } from '@hooks/useFollowing';
import { useFriends } from '@hooks/useFriends';
import { useFriendship } from '@hooks/useFriendship';
import { useParlayTail } from '@hooks/useParlayTail';
import { useUserProfile } from '@hooks/useUserProfile';
import type { BetsType, TimePeriod } from '@parlay-picker/shared/hooks';
import type { SocialLinks } from '@parlay-picker/shared/socialLinks';
import { Lock, Share2, X } from 'lucide-react';
import { type ReactElement, useEffect, useState } from 'react';
import { FollowerBenefitsPreview } from '../FollowerBenefitsPreview';
import LoadingSpinner from './LoadingSpinner';

// =============================================================================
// Types
// =============================================================================

interface UserProfileModalProps {
  userId?: string;
  displayName?: string;
  timePeriod?: TimePeriod;
  betsType?: BetsType;
  onClose: () => void;
}

type ProfileTab = 'profile' | 'followers' | 'following' | 'friends';
type AnalysisLevel = 'legs' | 'parlays';

// =============================================================================
// Component
// =============================================================================

export function UserProfileModal({
  userId,
  displayName,
  timePeriod = '30d',
  betsType = 'all',
  onClose,
}: UserProfileModalProps): ReactElement {
  const { profile, loading, error, reload } = useUserProfile({
    userId,
    displayName,
    timePeriod,
    betsType,
  });
  const fullProfile = profile && !profile.limited ? profile : null;
  const { user: currentUser } = useAuth();
  const { showToast } = useToast();
  const { isLocked, isAdmin } = useFeatureAccess();
  const followLocked = isLocked('social_follow');
  const [showFollowUpgrade, setShowFollowUpgrade] = useState(false);

  // Use userId if available, otherwise use profile userId once loaded
  const followTargetId = userId || profile?.user.id;
  const {
    isFollowing: hookIsFollowing,
    loading: followLoading,
    followUser,
    unfollowUser,
    checkFollowStatus,
  } = useFollow(followTargetId || '');

  // Friendship hook
  const friendLocked = isLocked('social_friend');
  const {
    status: friendshipStatus,
    loading: friendshipLoading,
    sendRequest: sendFriendRequest,
    acceptRequest: acceptFriendRequest,
    rejectRequest: rejectFriendRequest,
    cancelRequest: cancelFriendRequest,
    unfriend,
    checkStatus: checkFriendshipStatus,
  } = useFriendship(followTargetId || '');

  // Block hook
  const { loading: blockLoading, blockUser } = useBlock(followTargetId || '');

  const [showBlockMenu, setShowBlockMenu] = useState(false);

  const isOwnProfile = !!currentUser && !!followTargetId && currentUser.id === followTargetId;

  const isFollowing = hookIsFollowing ?? fullProfile?.isFollowing ?? false;

  // The server sends only what this viewer may see; visibility says which parts that is.
  const perms = fullProfile?.user.followerPermissions;
  const canSeePropBreakdown = fullProfile?.visibility.propBreakdown ?? false;
  const canSeeRecentBets = fullProfile?.visibility.recentBets ?? false;
  const canSeeFinancialDetails = fullProfile?.visibility.financialDetails ?? false;
  const [showProfileUpgrade, setShowProfileUpgrade] = useState(false);
  const { tailParlay } = useParlayTail();

  // Tab state
  const [activeTab, setActiveTab] = useState<ProfileTab>('profile');
  const [level, setLevel] = useState<AnalysisLevel>('parlays');

  // Followers/following data
  const profileDisplayName = profile?.user.displayName;
  const listsDisplayName = fullProfile?.user.displayName;
  const {
    followers,
    loading: followersLoading,
    loadMore: loadMoreFollowers,
    hasMore: hasMoreFollowers,
  } = useFollowers(listsDisplayName);
  const {
    following,
    loading: followingLoading,
    loadMore: loadMoreFollowing,
    hasMore: hasMoreFollowing,
  } = useFollowing(listsDisplayName);

  // Friends data (own profile only)
  const {
    friends,
    total: friendCount,
    loading: friendsLoading,
    loadFriends,
    loadMore: loadMoreFriends,
    hasMore: hasMoreFriends,
  } = useFriends();

  const [optimisticFollowerCount, setOptimisticFollowerCount] = useState<number | null>(null);
  const [tailingParlayId, setTailingParlayId] = useState<string | null>(null);
  const [expandedAnalysisId, setExpandedAnalysisId] = useState<string | null>(null);

  useEffect(() => {
    if (profile) {
      setOptimisticFollowerCount(profile.user.followerCount);
    }
  }, [profile]);

  useEffect(() => {
    if (currentUser && !isOwnProfile && followTargetId) {
      checkFollowStatus();
      checkFriendshipStatus();
    }
  }, [currentUser, isOwnProfile, followTargetId, checkFollowStatus, checkFriendshipStatus]);

  const handleFollowToggle = async () => {
    if (!profile) return;
    const wasFollowing = isFollowing;
    const originalCount = optimisticFollowerCount ?? profile.user.followerCount;
    setOptimisticFollowerCount(wasFollowing ? originalCount - 1 : originalCount + 1);
    try {
      if (wasFollowing) {
        await unfollowUser();
      } else {
        await followUser();
      }
      // What a follower may see is decided by the server, so refetch.
      await reload();
    } catch (_err) {
      setOptimisticFollowerCount(originalCount);
    }
  };

  const handleTailParlay = async (parlayId: string) => {
    setTailingParlayId(parlayId);
    try {
      await tailParlay(parlayId);
    } finally {
      setTailingParlayId(null);
    }
  };

  const handleCopyProfileLink = async () => {
    if (!profileDisplayName) return;
    const url = `${window.location.origin}/profile/${encodeURIComponent(profileDisplayName)}`;
    try {
      await navigator.clipboard.writeText(url);
      showToast('Profile link copied to clipboard!', 'success');
    } catch {
      showToast('Failed to copy link — try copying the URL manually.', 'error');
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="relative w-full max-w-4xl max-h-[90vh] mx-4 bg-theme-bg rounded-xl border border-theme-border overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-theme-border">
            <h2 className="text-2xl font-bold text-theme-text">User Profile</h2>
            <div className="flex items-center gap-2">
              {profileDisplayName && (
                <button
                  type="button"
                  onClick={handleCopyProfileLink}
                  className="text-theme-text-secondary hover:text-theme-text transition-colors p-2"
                  title="Copy profile link"
                >
                  <Share2 className="w-5 h-5" />
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="text-theme-text-secondary hover:text-theme-text transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          {fullProfile && (
            <div className="flex border-b border-theme-border px-6 overflow-x-auto scrollbar-none">
              {(['profile', 'followers', 'following'] as const).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  data-testid={`profile-tab-${tab}`}
                  data-active={activeTab === tab ? 'true' : 'false'}
                  onClick={() => {
                    if (tab === 'followers' || tab === 'following') setActiveTab(tab);
                    else setActiveTab(tab);
                  }}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap shrink-0 ${
                    activeTab === tab
                      ? 'border-theme-primary text-theme-primary'
                      : 'border-transparent text-theme-text-secondary hover:text-theme-text'
                  }`}
                >
                  {tab === 'profile' && 'Profile'}
                  {tab === 'followers' &&
                    `Followers (${optimisticFollowerCount ?? fullProfile.user.followerCount})`}
                  {tab === 'following' && `Following (${fullProfile.user.followingCount})`}
                </button>
              ))}
              {isOwnProfile && (
                <button
                  type="button"
                  data-testid="profile-tab-friends"
                  data-active={activeTab === 'friends' ? 'true' : 'false'}
                  onClick={() => {
                    setActiveTab('friends');
                    if (friends.length === 0) loadFriends();
                  }}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap shrink-0 ${
                    activeTab === 'friends'
                      ? 'border-theme-primary text-theme-primary'
                      : 'border-transparent text-theme-text-secondary hover:text-theme-text'
                  }`}
                >
                  Friends ({friendCount || 0})
                </button>
              )}
            </div>
          )}

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-88px)]">
            {loading && (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner />
              </div>
            )}

            {error && (
              <div className="p-6">
                <div className="bg-theme-danger/10 border border-theme-danger/20 rounded-lg p-4 text-center">
                  <p className="text-theme-danger">{error}</p>
                </div>
              </div>
            )}

            {/* Profile Tab */}
            {profile && activeTab === 'profile' && (
              <div className="p-6 space-y-6">
                <ProfileHeader
                  user={{
                    ...profile.user,
                    socialLinks: fullProfile?.user.socialLinks as SocialLinks | null | undefined,
                  }}
                  creatorInfo={profile.creatorInfo}
                  overallStats={profile.overallStats}
                  isOwnProfile={isOwnProfile}
                  currentUserId={currentUser?.id}
                  isFollowing={isFollowing}
                  followLocked={followLocked}
                  followLoading={followLoading}
                  friendLocked={friendLocked}
                  friendshipLoading={friendshipLoading}
                  friendshipStatus={friendshipStatus}
                  blockLoading={blockLoading}
                  showBlockMenu={showBlockMenu}
                  optimisticFollowerCount={optimisticFollowerCount}
                  friendCount={friendCount || 0}
                  onFollowToggle={handleFollowToggle}
                  onShowFollowUpgrade={() => setShowFollowUpgrade(true)}
                  onSendFriendRequest={async () => {
                    const result = await sendFriendRequest();
                    if (result.autoAccepted) {
                      showToast('You are now friends!', 'success');
                    } else {
                      showToast('Friend request sent', 'success');
                    }
                  }}
                  onCancelFriendRequest={async () => {
                    await cancelFriendRequest();
                    showToast('Friend request cancelled', 'info');
                  }}
                  onAcceptFriendRequest={async () => {
                    await acceptFriendRequest();
                    showToast('Friend request accepted!', 'success');
                  }}
                  onRejectFriendRequest={async () => {
                    await rejectFriendRequest();
                    showToast('Friend request declined', 'info');
                  }}
                  onUnfriend={async () => {
                    if (window.confirm(`Unfriend ${profile?.user.displayName}?`)) {
                      await unfriend();
                      showToast('Unfriended', 'info');
                    }
                  }}
                  onBlockUser={async () => {
                    if (
                      window.confirm(
                        `Block ${profile?.user.displayName}? This will remove any friendship, follows, and shared parlays.`,
                      )
                    ) {
                      await blockUser();
                      setShowBlockMenu(false);
                      showToast('User blocked', 'info');
                      onClose();
                    }
                  }}
                  onToggleBlockMenu={() => setShowBlockMenu(!showBlockMenu)}
                />

                {profile.limited && (
                  <>
                    <ProfileStatsSection
                      level="parlays"
                      onLevelChange={setLevel}
                      overallStats={profile.overallStats}
                      legStats={null}
                      canSeeFinancialDetails={false}
                    />
                    <div
                      data-testid="profile-limited-notice"
                      className="bg-theme-surface/30 rounded-lg p-5 text-center border border-dashed border-theme-border space-y-3"
                    >
                      <Lock className="w-5 h-5 text-theme-text-tertiary mx-auto" />
                      <p className="text-sm text-theme-text-secondary">
                        Recent bets, prop breakdowns and leg stats are on Pro.
                      </p>
                      {currentUser ? (
                        <button
                          type="button"
                          onClick={() => setShowProfileUpgrade(true)}
                          className="inline-flex items-center gap-1.5 text-sm text-theme-primary hover:underline"
                        >
                          <TierBadge feature="social_profile_view" size="sm" />
                          Upgrade to see the full profile
                        </button>
                      ) : (
                        <a href="/login" className="text-sm text-theme-primary hover:underline">
                          Log in to see more
                        </a>
                      )}
                    </div>
                  </>
                )}

                {fullProfile && (
                  <>
                    {/* Follower Benefits Preview */}
                    {currentUser && !isOwnProfile && !isFollowing && (
                      <FollowerBenefitsPreview
                        permissions={fullProfile.user.followerPermissions ?? null}
                        onFollow={handleFollowToggle}
                        isAdminViewer={isAdmin}
                      />
                    )}

                    <ProfileStatsSection
                      level={level}
                      onLevelChange={setLevel}
                      overallStats={fullProfile.overallStats}
                      legStats={fullProfile.legStats}
                      canSeeFinancialDetails={canSeeFinancialDetails}
                    />

                    <ProfileActivitySection
                      level={level}
                      recentBets={fullProfile.recentBets}
                      propBreakdown={fullProfile.propBreakdown}
                      legPropBreakdown={fullProfile.legPropBreakdown}
                      overallStats={fullProfile.overallStats}
                      canSeePropBreakdown={canSeePropBreakdown}
                      isFollowing={isFollowing}
                      followLocked={followLocked}
                      displayName={profile.user.displayName}
                      onFollowToggle={handleFollowToggle}
                      onShowFollowUpgrade={() => setShowFollowUpgrade(true)}
                    />

                    {/* Recent Bets */}
                    {(fullProfile.recentBets.length > 0 || !canSeeRecentBets) && (
                      <div>
                        <h4 className="text-sm font-semibold text-theme-text-secondary uppercase tracking-wide mb-3">
                          Recent Bets
                        </h4>
                        {!canSeeRecentBets ? (
                          <div className="bg-theme-surface/30 rounded-lg p-5 text-center border border-dashed border-theme-border space-y-2">
                            <Lock className="w-5 h-5 text-theme-text-tertiary mx-auto" />
                            <p className="text-sm text-theme-text-secondary">
                              {isFollowing
                                ? "This user hasn't shared their recent bets"
                                : 'Follow to see recent bets'}
                            </p>
                            {!isFollowing &&
                              (followLocked ? (
                                <button
                                  type="button"
                                  onClick={() => setShowFollowUpgrade(true)}
                                  className="inline-flex items-center gap-1.5 text-xs text-theme-text-secondary hover:text-theme-text transition-colors"
                                >
                                  <TierBadge feature="social_follow" size="sm" />
                                  Upgrade to follow
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={handleFollowToggle}
                                  className="text-xs text-theme-primary hover:underline"
                                >
                                  Follow {profile.user.displayName}
                                </button>
                              ))}
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {fullProfile.recentBets.map((bet) => {
                              const canSeeAnalysis =
                                currentUser?.id === userId ||
                                (isFollowing && perms?.showDetailedAnalysis);

                              return (
                                <RecentBetCard
                                  key={bet.id}
                                  bet={bet}
                                  isFollowing={isFollowing}
                                  followerPermissions={perms}
                                  isTailing={tailingParlayId === bet.id}
                                  isAnalysisExpanded={expandedAnalysisId === bet.id}
                                  canSeeAnalysis={!!canSeeAnalysis}
                                  followLocked={followLocked}
                                  isOwnBet={isOwnProfile}
                                  onTail={handleTailParlay}
                                  onToggleAnalysis={(betId) =>
                                    setExpandedAnalysisId(
                                      expandedAnalysisId === betId ? null : betId,
                                    )
                                  }
                                  onShowFollowUpgrade={() => setShowFollowUpgrade(true)}
                                  onFollow={handleFollowToggle}
                                  displayName={profile.user.displayName}
                                />
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Followers Tab */}
            {profile && activeTab === 'followers' && (
              <UserListTab
                users={followers}
                loading={followersLoading}
                hasMore={hasMoreFollowers}
                emptyMessage="No followers yet"
                onLoadMore={loadMoreFollowers}
                onViewProfile={onClose}
              />
            )}

            {/* Following Tab */}
            {profile && activeTab === 'following' && (
              <UserListTab
                users={following}
                loading={followingLoading}
                hasMore={hasMoreFollowing}
                emptyMessage="Not following anyone yet"
                onLoadMore={loadMoreFollowing}
                onViewProfile={onClose}
              />
            )}

            {/* Friends Tab (own profile only) */}
            {profile && activeTab === 'friends' && isOwnProfile && (
              <UserListTab
                users={friends.map((friend) => ({
                  id: friend.id,
                  displayName: friend.displayName,
                  firstName: '',
                  lastName: '',
                  isVerified: friend.isVerified,
                  followerCount: friend.followerCount,
                  followingCount: 0,
                  isFollowedByCurrentUser: false,
                }))}
                loading={friendsLoading}
                hasMore={hasMoreFriends}
                emptyMessage="No friends yet"
                emptySubMessage="Send friend requests from user profiles"
                onLoadMore={loadMoreFriends}
                onViewProfile={onClose}
              />
            )}
          </div>
        </div>
      </div>

      {showProfileUpgrade && (
        <UpgradeModal
          mode="feature"
          feature="social_profile_view"
          onDismiss={() => setShowProfileUpgrade(false)}
        />
      )}

      {showFollowUpgrade && (
        <UpgradeModal
          mode="feature"
          feature="social_follow"
          onDismiss={() => setShowFollowUpgrade(false)}
        />
      )}
    </>
  );
}
